// ─────────────────────────────────────────────────────────────────────────────
// Engine Wire Contract — the identity mechanism.
//
// Every jid / message-key field on the engine wire is typed with one of the
// marker leaf schemas (WireJidSchema, WireWidSchema, WireKeySchema). `wireIdentityOf(schema)`
// derives a schema's identity spec from those markers once, at load time, and
// `mapWireIdentity` is the ONLY code in either engine that rewrites an identity
// field: each engine passes its own id-resolution function(s) and nothing else.
//
// Addressing rule (the contract): a person is `<digits>@c.us` whenever the
// engine can resolve it; `@g.us`, `@newsletter`, `@broadcast` pass unchanged;
// a raw `<n>@lid` only on a genuine resolver miss. Key strings are
// `<fromMe>_<remote>_<id>[_<participant>]` (utils/message-key.ts).
// ─────────────────────────────────────────────────────────────────────────────

import { z } from 'zod';
import { parseWaMessageKey, serializeWaMessageKey, type WaMessageKey } from '../utils/message-key';

export type WireLeaf = 'jid' | 'key'; // 'key' = a key STRING

/** Field name → leaf, or field name → nested spec. The key `'[]'` means
 *  "iterate this value" (an array); its node applies to every element. */
export interface WireIdentitySpec { readonly [field: string]: WireLeaf | WireIdentitySpec }

/** Marker for a jid field. Recognised BY REFERENCE: never call `.describe()` /
 *  `.brand()` on it (both create a new instance the derivation cannot see).
 *  Wrapping it (`.optional()`, `.nullable()`, `z.array(...)`) is fine. */
export const WireJidSchema = z.string();
/** Marker for a jid field that the engines emit as a wid object
 *  (`{_serialized, user?, server?}` — the listener identity fields). The same
 *  'jid' leaf as WireJidSchema; the walker rebuilds the wid from the mapped jid.
 *  Same reference rule as WireJidSchema. */
export const WireWidSchema = z.object({ _serialized: z.string() }).passthrough();
/** Marker for a message-key STRING field. Same reference rule as WireJidSchema. */
export const WireKeySchema = z.string();

type SpecNode = WireLeaf | WireIdentitySpec;

/** The spec key meaning "iterate this value". */
const ARRAY = '[]';

/** Deep-merges two spec nodes (union members, intersection sides). A leaf/spec
 *  or jid/key clash is a schema bug: throw. */
function mergeNodes(a: SpecNode, b: SpecNode, at: string): SpecNode {
    if (typeof a === 'string' || typeof b === 'string') {
        if (a === b) return a;
        throw new Error(`wire-identity: conflicting identity at "${at}"`);
    }
    const out: Record<string, SpecNode> = { ...a };
    for (const [k, v] of Object.entries(b)) {
        out[k] = k in out ? mergeNodes(out[k], v, `${at}.${k}`) : v;
    }
    return out;
}

const K = z.ZodFirstPartyTypeKind;
/** Kinds that cannot hold a marker (the leaf kinds the contract schemas use). */
const LEAF_KINDS: ReadonlySet<string> = new Set([
    K.ZodString, K.ZodNumber, K.ZodBoolean, K.ZodDate, K.ZodNull, K.ZodAny,
    K.ZodUnknown, K.ZodVoid, K.ZodLiteral, K.ZodEnum,
]);

/** A property of an untyped value (`schema._def` is `any` in zod's types). */
function prop(value: unknown, key: string): unknown {
    return typeof value === 'object' && value !== null ? (value as Record<string, unknown>)[key] : undefined;
}

function isSchema(value: unknown): value is z.ZodTypeAny {
    return typeof value === 'object' && value !== null && '_def' in value;
}

/** The child schema a def holds under `key` (throws on a malformed def). */
function childOf(def: unknown, key: string, at: string): z.ZodTypeAny {
    const child = prop(def, key);
    if (!isSchema(child)) throw new Error(`wire-identity: no ${key} schema at "${at}"`);
    return child;
}

function derive(schema: z.ZodTypeAny, at: string): SpecNode | null {
    if (schema === WireJidSchema || schema === WireWidSchema) return 'jid';
    if (schema === WireKeySchema) return 'key';
    const def: unknown = schema._def;
    const typeName = prop(def, 'typeName');
    switch (typeName) {
        case K.ZodObject: {
            const shape: unknown = (schema as z.AnyZodObject).shape;
            const out: Record<string, SpecNode> = {};
            for (const [field, child] of Object.entries(typeof shape === 'object' && shape !== null ? shape : {})) {
                if (!isSchema(child)) continue;
                const node = derive(child, `${at}.${field}`);
                if (node) out[field] = node;
            }
            return Object.keys(out).length ? out : null;
        }
        case K.ZodArray: {
            const node = derive(childOf(def, 'type', at), `${at}[]`);
            return node ? { [ARRAY]: node } : null;
        }
        case K.ZodOptional:
        case K.ZodNullable:
        case K.ZodDefault:
            return derive(childOf(def, 'innerType', at), at);
        case K.ZodEffects:
            return derive(childOf(def, 'schema', at), at);
        case K.ZodUnion:
        case K.ZodDiscriminatedUnion: {
            const options = prop(def, 'options');
            const members: unknown[] = Array.isArray(options) ? options : options instanceof Map ? [...options.values()] : [];
            return mergeAll(members.filter(isSchema), at);
        }
        case K.ZodIntersection:
            return mergeAll([childOf(def, 'left', at), childOf(def, 'right', at)], at);
        case K.ZodRecord:
            // A record's keys are data, so a marker among its values has no path.
            if (derive(childOf(def, 'valueType', at), `${at}{}`)) throw new Error(`wire-identity: a marker under ZodRecord at "${at}" has no path`);
            return null;
        default:
            if (typeof typeName === 'string' && LEAF_KINDS.has(typeName)) return null;
            // Fail loudly: an unhandled kind could hide a marker.
            throw new Error(`wire-identity: ${String(typeName)} at "${at}" is not supported by wireIdentityOf`);
    }
}

function mergeAll(members: z.ZodTypeAny[], at: string): SpecNode | null {
    let acc: SpecNode | null = null;
    for (const m of members) {
        const node = derive(m, at);
        if (node) acc = acc ? mergeNodes(acc, node, at) : node;
    }
    return acc;
}

/** The identity spec of a schema, derived from its marker leaves. Call once per
 *  schema at load / registration time and keep the result as a constant. */
export function wireIdentityOf(schema: z.ZodTypeAny): WireIdentitySpec {
    const node = derive(schema, '$');
    if (typeof node === 'string') throw new Error('wire-identity: a bare marker has no path; wrap it in an object');
    return node ?? {};
}

/** The jid string of a jid value: the string itself, or a wid's `_serialized`. */
const jidString = (v: unknown): string | undefined =>
    typeof v === 'string' ? v : typeof (v as { _serialized?: unknown } | null)?._serialized === 'string'
        ? (v as { _serialized: string })._serialized : undefined;

/**
 * Rewrites every identity field `spec` names in `payload`: one recursive
 * copy-on-write pass. Unchanged nodes are returned by reference (a live
 * engine model is never mutated); a changed node is rebuilt as a plain shallow
 * copy. Each distinct jid / key is resolved once per call (call-local memo).
 *
 * Rules:
 *  - jid, string → `mapJid(value)`; jid, wid-like `{_serialized}` → the wid
 *    with `_serialized`/`user`/`server` rebuilt from the mapped jid.
 *  - key string → parsed; its `remote`/`participant` go through `mapJid`
 *    (sharing the jid memo), or the whole key through `mapKey` when given;
 *    then re-serialized. An unparseable string is left unchanged.
 *  - key object (a node whose `_serialized` is a key leaf, carrying `id` and
 *    `remote` parts) → its parts are mapped and its `_serialized` is always
 *    re-derived from them with the one serializer; `mapKey` does not apply.
 *  - null / undefined / missing values are skipped, never created.
 *  - A mapper rejection leaves the value unchanged. The walker never throws.
 */
export async function mapWireIdentity<T>(
    spec: WireIdentitySpec,
    payload: T,
    /** The engine's jid resolver. Contract: it resolves to a jid string and, on
     *  a resolver miss, returns its input unchanged (never `undefined`). */
    mapJid: (jid: string) => Promise<string>,
    /** The engine's key resolver (pod inbound only). Contract: it resolves to a
     *  key and, on a miss, returns its input unchanged. */
    mapKey?: (key: WaMessageKey) => Promise<WaMessageKey>,
): Promise<T> {
    const jidMemo = new Map<string, Promise<string>>();
    const keyMemo = new Map<string, Promise<string>>();

    const resolveJid = (jid: string): Promise<string> => {
        let p = jidMemo.get(jid);
        if (!p) {
            p = Promise.resolve().then(() => mapJid(jid)).then((r) => r, () => jid);
            jidMemo.set(jid, p);
        }
        return p;
    };

    const resolveKey = (serialized: string): Promise<string> => {
        let p = keyMemo.get(serialized);
        if (!p) {
            p = (async () => {
                const parsed = parseWaMessageKey(serialized);
                if (!parsed) return serialized;
                if (mapKey) {
                    return serializeWaMessageKey(await Promise.resolve().then(() => mapKey(parsed)).catch(() => parsed));
                }
                const [remoteJid, participant] = await Promise.all([
                    resolveJid(parsed.remoteJid),
                    parsed.participant ? resolveJid(parsed.participant) : undefined,
                ]);
                return serializeWaMessageKey({ ...parsed, remoteJid, participant });
            })();
            keyMemo.set(serialized, p);
        }
        return p;
    };

    const mapJidValue = async (value: unknown): Promise<unknown> => {
        if (typeof value === 'string') return resolveJid(value);
        const serialized = jidString(value);
        if (serialized === undefined) return value;
        const w = await resolveJid(serialized);
        if (w === serialized) return value;
        const at = w.lastIndexOf('@');
        return { ...(value as object), _serialized: w, user: at < 0 ? w : w.slice(0, at), server: at < 0 ? '' : w.slice(at + 1) };
    };

    const walk = async (node: SpecNode, value: unknown): Promise<unknown> => {
        if (value === null || value === undefined) return value;
        if (node === 'jid') return mapJidValue(value);
        if (node === 'key') return typeof value === 'string' ? resolveKey(value) : value;
        if (typeof value !== 'object') return value;
        if (ARRAY in node) {
            if (!Array.isArray(value)) return value;
            const next = await Promise.all(value.map((v) => walk(node[ARRAY], v)));
            return next.some((v, i) => v !== value[i]) ? next : value;
        }

        const obj = value as Record<string, unknown>;
        const isKeyObject = node._serialized === 'key' && typeof obj._serialized === 'string'
            && typeof obj.id === 'string' && obj.remote != null;
        const fields = Object.entries(node).filter(([field]) => !(isKeyObject && field === '_serialized'));
        const mapped = await Promise.all(fields.map(async ([field, sub]) => [field, await walk(sub, obj[field])] as const));

        let out = obj;
        const set = (field: string, v: unknown) => {
            if (v === obj[field]) return;
            if (out === obj) out = { ...obj };
            out[field] = v;
        };
        for (const [field, v] of mapped) set(field, v);
        if (isKeyObject) {
            set('_serialized', serializeWaMessageKey({
                fromMe: obj.fromMe as boolean,
                remoteJid: jidString(out.remote),
                id: obj.id as string,
                participant: jidString(out.participant),
            }));
        }
        return out;
    };

    return (await walk(spec, payload)) as T;
}
